import { SuggestionItem } from '../types';

export const EC2_INSTANCE_SUGGESTIONS: SuggestionItem[] = [
  {
    id: 'ec2.instanceList',
    label: 'EC2 Instance List',
    description: 'List all EC2 instances with ID, type, state, and IPs',
    keywords: ['ec2', 'instances', 'instance list', 'nodes', 'node list', 'ec2 list'],
    category: 'ec2-instances',
    queryId: 'RESOURCE_INFO',
    fieldPath: 'domain.resources.ALL.ec2Instances',
    formatter: (instances) => {
      if (!instances || instances.length === 0) return '⚠️ No EC2 instances found.';
      const rows = instances.slice(0, 20).map((e: any) =>
        `| ${e.instanceId || 'N/A'} | ${e.instanceType || 'N/A'} | ${e.nodeType || 'N/A'} | ${e.state || 'N/A'} | ${e.privateIp || 'N/A'} |`
      );
      return [
        `## 🖥️ EC2 Instances (${instances.length} total, showing ${Math.min(instances.length, 20)})`,
        '| Instance ID | Type | Node Type | State | Private IP |',
        '|-------------|------|-----------|-------|------------|',
        ...rows,
      ].join('\n');
    },
  },
  {
    id: 'ec2.instanceTypes',
    label: 'Instance Types',
    description: 'Instance types used in the domain',
    keywords: ['instance type', 'instance types', 'ec2 type', 'machine type'],
    category: 'ec2-instances',
    queryId: 'RESOURCE_INFO',
    fieldPath: 'domain.resources.ALL.ec2Instances',
    formatter: (instances) => {
      if (!instances || instances.length === 0) return '⚠️ No instances found.';
      const typeCounts: Record<string, number> = {};
      instances.forEach((e: any) => {
        const t = e.instanceType || 'unknown';
        typeCounts[t] = (typeCounts[t] || 0) + 1;
      });
      const rows = Object.entries(typeCounts).map(([t, c]) => `| ${t} | ${c} |`);
      return ['**Instance Types:**', '| Type | Count |', '|------|-------|', ...rows].join('\n');
    },
  },
  {
    id: 'ec2.nodeTypes',
    label: 'Node Types',
    description: 'Node type breakdown (data, master, etc.)',
    keywords: ['node type', 'node types', 'data node', 'master node', 'warm node', 'ultrawarm'],
    category: 'ec2-instances',
    queryId: 'RESOURCE_INFO',
    fieldPath: 'domain.resources.ALL.ec2Instances',
    formatter: (instances) => {
      if (!instances || instances.length === 0) return '⚠️ No instances found.';
      const typeCounts: Record<string, number> = {};
      instances.forEach((e: any) => {
        const t = e.nodeType || 'unknown';
        typeCounts[t] = (typeCounts[t] || 0) + 1;
      });
      const rows = Object.entries(typeCounts).map(([t, c]) => `| ${t} | ${c} |`);
      return ['**Node Types:**', '| Node Type | Count |', '|-----------|-------|', ...rows].join('\n');
    },
  },
  {
    id: 'ec2.elbHealth',
    label: 'ELB Health Summary',
    description: 'ELB health status across all instances',
    keywords: ['elb health', 'load balancer health', 'elb status', 'unhealthy nodes'],
    category: 'ec2-instances',
    queryId: 'RESOURCE_INFO',
    fieldPath: 'domain.resources.ALL.ec2Instances',
    formatter: (instances) => {
      if (!instances || instances.length === 0) return '⚠️ No instances found.';
      const healthCounts: Record<string, number> = {};
      instances.forEach((e: any) => {
        const h = e.elbHealth || 'unknown';
        healthCounts[h] = (healthCounts[h] || 0) + 1;
      });
      const rows = Object.entries(healthCounts).map(([h, c]) => `| ${h} | ${c} |`);
      return ['**ELB Health Summary:**', '| Status | Count |', '|--------|-------|', ...rows].join('\n');
    },
  },
  {
    id: 'ec2.azDistribution',
    label: 'AZ Distribution',
    description: 'Instance distribution across availability zones',
    keywords: ['az distribution', 'availability zone', 'az', 'zone distribution'],
    category: 'ec2-instances',
    queryId: 'RESOURCE_INFO',
    fieldPath: 'domain.resources.ALL.ec2Instances',
    formatter: (instances) => {
      if (!instances || instances.length === 0) return '⚠️ No instances found.';
      const azCounts: Record<string, number> = {};
      instances.forEach((e: any) => {
        const az = e.az || 'unknown';
        azCounts[az] = (azCounts[az] || 0) + 1;
      });
      const rows = Object.entries(azCounts).map(([az, c]) => `| ${az} | ${c} |`);
      return ['**AZ Distribution:**', '| AZ | Count |', '|----|-------|', ...rows].join('\n');
    },
  },
  {
    id: 'ec2.ec2States',
    label: 'EC2 State Summary',
    description: 'EC2 instance state breakdown (running, stopped, etc.)',
    keywords: ['ec2 state', 'instance state', 'running', 'stopped', 'terminated'],
    category: 'ec2-instances',
    queryId: 'RESOURCE_INFO',
    fieldPath: 'domain.resources.ALL.ec2Instances',
    formatter: (instances) => {
      if (!instances || instances.length === 0) return '⚠️ No instances found.';
      const stateCounts: Record<string, number> = {};
      instances.forEach((e: any) => {
        const s = e.state || 'unknown';
        stateCounts[s] = (stateCounts[s] || 0) + 1;
      });
      const rows = Object.entries(stateCounts).map(([s, c]) => `| ${s} | ${c} |`);
      return ['**EC2 State Summary:**', '| State | Count |', '|-------|-------|', ...rows].join('\n');
    },
  },
  {
    id: 'ec2.diNumbers',
    label: 'DI Numbers',
    description: 'List of DI numbers across instances',
    keywords: ['di', 'di number', 'di numbers', 'deployment instance'],
    category: 'ec2-instances',
    queryId: 'RESOURCE_INFO',
    fieldPath: 'domain.resources.ALL.ec2Instances',
    formatter: (instances) => {
      if (!instances || instances.length === 0) return '⚠️ No instances found.';
      const dis = [...new Set(instances.map((e: any) => e.diNumber).filter(Boolean))].sort();
      return `**DI Numbers (${dis.length} unique):** ${dis.join(', ')}`;
    },
  },
];
